import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { navigate } from "vike/client/router"
import UpdateController from "../../controller/updates-controller"

// Import step components
import UploadStep from "./steps/UploadStep"
import ReviewStep from "./steps/ReviewStep"
import ImportSettingsStep from "./steps/ImportSettingsStep"

// Import shared components
import StepHeader from "./components/StepHeader"
import StepNavigation from "./components/StepNavigation"

// Import constants
import { STEPS, FILE_MODES, DB_MODES } from "./constants"

const controller = UpdateController()

export default function UpdateImportWizard({ onClose }) {
  const [currentStep, setCurrentStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [manifest, setManifest] = useState(null)
  const [updateKey, setUpdateKey] = useState(null)
  const [uploadError, setUploadError] = useState([])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [currentActivity, setCurrentActivity] = useState("")

  // Import settings
  const [fileMode, setFileMode] = useState(FILE_MODES.find((mode) => mode.value === "SKIP"))
  const [dbMode, setDbMode] = useState(DB_MODES.find((mode) => mode.value === "SKIP"))

  // Handle file upload
  const handleFileUpload = async (file) => {
    setIsLoading(true)
    setCurrentActivity("Uploading update package...")
    setUploadProgress(0)
    setUploadError([])

    try {
      const result = await controller.uploadUpdate({
        file,
        setProgress: (progress) => {
          setUploadProgress(progress)
          setCurrentActivity(`Uploading update package: ${progress}%`)
        },
        onResponse: (response) => {
          setCurrentActivity(response.activity || "Processing update package...")
        },
      })

      if (result.error) {
        setUploadError([result.error])
        showNotification({
          message: result.error,
          className: "bg-admin_error text-white",
        })
        return
      }

      if (result.result?.manifest && result.result["update-key"]) {
        setManifest({
          ...result.result.manifest,
          filename: file.name,
        })
        setUpdateKey(result.result["update-key"])
        showNotification({
          message: "Package uploaded successfully",
          className: "bg-admin_success text-white",
        })
        // Automatically proceed to step 2
        setCurrentStep(2)
      }
    } catch (error) {
      const errorMessage = "Failed to process update file: " + error.message
      setUploadError([errorMessage])
      showNotification({
        message: errorMessage,
        className: "bg-admin_error text-white",
      })
    } finally {
      setIsLoading(false)
      setCurrentActivity("")
    }
  }

  // Handle import
  const handleImport = async () => {
    setIsLoading(true)
    setCurrentActivity("Starting import process...")

    try {
      const result = await controller.loadUpdate({
        updateKey,
        fileMode: fileMode.value,
        dbMode: dbMode.value,
        onResponse: (response) => {
          setCurrentActivity(response.activity || "Importing update...")
        },
      })

      if (result.error) {
        showPromptDialog({
          title: "Import Failed",
          message: result.error,
          showYesBtn: true,
          showNoBtn: false,
          yesLabel: "Close",
        })
      } else {
        showPromptDialog({
          title: "🎉 Import Complete!",
          message: "Update imported successfully. The changes are now live on your system.",
          yesLabel: "Done",
          noLabel: "Import Another",
          onConfirm: () => {
            navigate("/admin/updates")
          },
          onCancel: () => {
            // Reset to step 1 for another import
            setCurrentStep(1)
            setManifest(null)
            setUpdateKey(null)
            setFileMode(FILE_MODES.find((mode) => mode.value === "SKIP"))
            setDbMode(DB_MODES.find((mode) => mode.value === "SKIP"))
          },
        })
      }
    } catch (error) {
      showPromptDialog({
        title: "Import Failed",
        message: "Failed to import update: " + error.message,
        showYesBtn: true,
        showNoBtn: false,
        yesLabel: "Close",
      })
    } finally {
      setIsLoading(false)
      setCurrentActivity("")
    }
  }

  // Handle step navigation
  const handleNext = () => {
    const nextStep = Math.min(currentStep + 1, 3)
    setCurrentStep(nextStep)
  }

  const handlePrevious = () => {
    const prevStep = Math.max(currentStep - 1, 1)
    setCurrentStep(prevStep)
  }

  const handleStepClick = (step) => {
    // Allow going back to completed steps
    if (step <= currentStep && manifest) {
      setCurrentStep(step)
    }
  }

  return (
    <div className="h-full flex flex-col bg-admin_canvas">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-admin_bordered bg-admin_paper">
        <div>
          <Typography 
            component="h1" 
            variant="admin_h3"
          >
            Import Site Update
          </Typography>

          <Typography 
            variant="admin_txt1_dim" 
            className="mt-1"
          >
            Import CMS content, localization, and files from an update package
          </Typography>
        </div>
      </div>

      {/* Step Indicator */}
      <StepHeader
        steps={STEPS}
        currentStep={currentStep}
        onStepClick={handleStepClick}
      />

      {/* Navigation */}
      <StepNavigation
        currentStep={currentStep}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onImport={handleImport}
        manifest={manifest}
        isLoading={isLoading}
      />

      {/* Scrollable Content */}
      <div className="flex-1 bg-admin_paper shadow-inner overflow-y-auto">
        <div className="max-w-2xl mx-auto p-6">
          {currentStep === 1 && (
            <UploadStep
              onFileUpload={handleFileUpload}
              uploadError={uploadError}
              isLoading={isLoading}
              uploadProgress={uploadProgress}
              currentActivity={currentActivity}
              manifest={manifest}
            />
          )}

          {currentStep === 2 && <ReviewStep manifest={manifest} />}

          {currentStep === 3 && (
            <ImportSettingsStep
              fileMode={fileMode}
              dbMode={dbMode}
              onFileModeChange={setFileMode}
              onDbModeChange={setDbMode}
              fileModeOptions={FILE_MODES}
              dbModeOptions={DB_MODES}
              manifest={manifest}
            />
          )}
        </div>
      </div>
    </div>
  )
}
